import type { Field } from "../Field";
import { initField } from "../Field";
import type { GenericSchema, InferOutput } from "valibot";
import { boolean, nullish, object, objectAsync, safeParse, string } from "valibot";
import { dataName, gridFSFile, label, required, resource } from "../../utils/validation/base";
import { TextInput } from "../../components/inputs/TextInput";
import { useTranslation } from "../../state/i18n";
import { EXISTS_CONDITION_KEYS, MAX_CHARACTERS_XL } from "../../constants";
import { useFormServices } from "../../services/formServices";
import CustomerResourceInput from "../CustomerResourceInput";
import { SEARCHABLE_FIELDS } from "../../utils/fieldUtil";
import MixedInput from "../../components/inputs/MixedInput";
import { toFieldValueOptions } from "../../utils/selectOptionsUtil";
import { resourceAsync } from "../../utils/validation/async";
import {
  parseStringTemplate,
  parseStringTemplateForField,
  stringTemplateToString,
} from "../../utils/stringTemplateUtil";
import { stringTemplate } from "../../utils/validation/stringTemplate";
import { SEARCHABLE_VARIABLES, toVariableValueOption } from "../../variables/variableUtil";
import { isFilledInFilter } from "../../pages/customer/form/filter";
import Image from "../valueRendering/Image";
import { createDynamicValueOptions } from "../../utils/menu/searchableMenuUtil";
import { FieldButtonPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { keyFromSelector } from "i18next";

const DtoSchema = object({
  label_text: string(),
  data_name: string(),
  required: boolean(),
  background_image: nullish(string()),
  filename: string(),
});

const ResourceSchema = objectAsync({
  background_image: resourceAsync(),
});

const DomainSchema = object({
  label_text: label(),
  data_name: dataName(),
  required: required(),
  background_image: resource(),
  filename: stringTemplate(SEARCHABLE_FIELDS, SEARCHABLE_VARIABLES, [], MAX_CHARACTERS_XL, false),
}) satisfies GenericSchema<InferOutput<typeof ResourceSchema>>;

const ValueSchema = gridFSFile();

export type DrawingFieldProperties = InferOutput<typeof DomainSchema>;
type DrawingFieldDtoType = InferOutput<typeof DtoSchema>;
type ValueType = InferOutput<typeof ValueSchema>;

const DrawingField: Field<DrawingFieldProperties, DrawingFieldDtoType, ValueType> = {
  type: "drawing",
  category: "media",
  widget: "com.moreapps.plugins:drawing:5",
  schema: DomainSchema,
  resourceSchema: ResourceSchema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    required: false,
    background_image: null,
    filename: [],
  }),
  duplicateProperties: (properties, updateReference) => ({
    ...properties,
    filename: properties.filename.map(updateReference),
  }),
  ruleBehaviour: {
    conditions: { keys: EXISTS_CONDITION_KEYS, variant: "none" },
    actions: { variant: "none" },
  },
  filter: {
    type: "none",
    definition: [isFilledInFilter],
  },
  stringValue: (value) => value,
  renderValue: (value, { fieldConfig }) => {
    const { t } = useTranslation();

    return (
      <Image
        fileId={value}
        scope="submission"
        ariaLabel={t(DrawingField.titleKey)}
        modalTitle={fieldConfig.properties.label_text}
        modalIcon={DrawingField.icon}
        modalCategory={DrawingField.category}
      />
    );
  },
  icon: "pencil",
  titleKey: keyFromSelector(($) => $.DRAWING_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.DRAWING_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    background_image: keyFromSelector(($) => $.FIELD_PROPERTY_BACKGROUND_IMAGE),
    filename: keyFromSelector(($) => $.FIELD_PROPERTY_FILE_NAME),
  },
  toDto: (domain, fields, variables) => {
    const { output } = safeParse(DtoSchema, {
      ...domain,
      filename: stringTemplateToString(domain.filename, fields, variables),
    });
    return output as DrawingFieldDtoType;
  },
  postProcessFromDto: (domain, fields, variables, scope) => ({
    ...domain,
    filename: parseStringTemplateForField(
      domain.filename,
      fields.filter((field) => field.scope === scope),
      variables,
    ),
  }),
  PreviewContent: (props) => {
    const { t } = useTranslation();

    return (
      <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
        <FieldButtonPreview icon="pencil" text={t(($) => $.DRAWING_FIELD_PREVIEW_ADD_BUTTON_LABEL)} />
      </PreviewContentLabel>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, variableService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            required
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            errorMessage={props.getError("label_text")}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_IMAGE)}>
          <CustomerResourceInput
            required={false}
            label={t(props.propertyKeys.background_image)}
            value={props.data.properties.background_image ?? null}
            errorMessage={props.getError("background_image")}
            onSelect={(file) => props.setValue("background_image", file.id)}
            onClear={() => props.setValue("background_image", null)}
            info={{
              title: t(props.propertyKeys.background_image),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.background_image }),
              description: t(($) => $.FIELD_PROPERTY_BACKGROUND_IMAGE_DESCRIPTION),
            }}
          />
          <MixedInput
            label={t(props.propertyKeys.filename)}
            info={{
              title: t(props.propertyKeys.filename),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.filename }),
              description: t(($) => $.FIELD_PROPERTY_FILE_NAME_DESCRIPTION),
            }}
            value={props.data.properties.filename}
            parse={(value) =>
              parseStringTemplate(
                value,
                fieldService.getFieldsByTypes(SEARCHABLE_FIELDS),
                variableService.getVariablesByTypes(SEARCHABLE_VARIABLES),
              )
            }
            onChange={(value) => props.setValue("filename", value)}
            options={createDynamicValueOptions(t, {
              fields: fieldService.getFieldsByTypes(SEARCHABLE_FIELDS).flatMap((field) => toFieldValueOptions(field)),
              subforms: [],
              customVariables: variableService.getVariablesByTypes(SEARCHABLE_VARIABLES).map(toVariableValueOption),
              metaVariables: [],
            })}
            errorMessage={props.getError("filename")}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(DrawingField);
